import { useEffect, useMemo, useState } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import {
  Dialog,
  Input,
  PageEyebrow,
  PageGrid,
  PageWideSection,
  TovButton,
  msg,
  useToast,
} from '@tov-plus/ui';

import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { GalleryLightbox } from './GalleryLightbox';
import {
  AlbumsRail,
  GalleryControls,
  GalleryEmptyState,
  GalleryPhotoGrid,
  MyUploadsPanel,
  type GalleryFeedFilter,
} from './GalleryPageSections';
import { GalleryUploadDialog } from './GalleryUploadDialog';
import type { GuestGalleryItem } from './galleryHelpers';
import { useGuestGallery, type GalleryErrorKind } from './useGuestGallery';

function galleryFallbackCopy(errorKind: GalleryErrorKind | null) {
  switch (errorKind) {
    case 'no_access':
      return {
        title: msg('pages.gallery_page.gallery_access_unavailable'),
        body: msg('pages.gallery_page.guest_does_not_have_access_to_gallery'),
        canRetry: false,
      };
    case 'not_available':
      return {
        title: msg('pages.gallery_page.gallery_not_available'),
        body: msg('pages.gallery_page.gallery_is_not_available_for_this_guest'),
        canRetry: false,
      };
    case 'network':
      return {
        title: msg('pages.gallery_page.connection_issue'),
        body: msg('pages.gallery_page.check_connection_and_try_again'),
        canRetry: true,
      };
    case 'unexpected':
    default:
      return {
        title: msg('pages.gallery_page.gallery_could_not_load'),
        body: msg('pages.gallery_page.try_again_in_a_moment'),
        canRetry: true,
      };
  }
}

export function GalleryPage() {
  const access = useGuestAccess();
  const notifications = useToast();
  const {
    status,
    gallery,
    reload,
    loadMore,
    updateGallery,
    isLoadingMore,
    errorKind,
  } = useGuestGallery();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);
  const [activeFeed, setActiveFeed] = useState<GalleryFeedFilter>('approved');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [gallerySearchQuery, setGallerySearchQuery] = useState('');
  const [favoriteItemIds, setFavoriteItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [pendingFavoriteItemIds, setPendingFavoriteItemIds] = useState<
    Set<string>
  >(() => new Set());
  const [pendingReactionItemIds, setPendingReactionItemIds] = useState<
    Set<string>
  >(() => new Set());
  const [savingCommentItemIds, setSavingCommentItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isCreatingSharePackage, setIsCreatingSharePackage] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [selectedUpload, setSelectedUpload] = useState<GuestGalleryItem | null>(
    null,
  );
  const [selectedUploadCaption, setSelectedUploadCaption] = useState('');
  const [isSavingUpload, setIsSavingUpload] = useState(false);
  const canUseGuestIdentity =
    access.status === 'ready' &&
    access.viewerMode !== 'public_visitor' &&
    access.viewerMode !== 'planner_public_preview';
  const favoritesStorageKey =
    canUseGuestIdentity && access.occasionSlug && access.guestToken
      ? `tov-plus:guest-gallery-favorites:${access.occasionSlug}:${access.guestToken}`
      : null;

  useEffect(() => {
    if (!favoritesStorageKey) {
      setFavoriteItemIds(new Set());
      return;
    }

    try {
      const parsed = JSON.parse(
        window.localStorage.getItem(favoritesStorageKey) ?? '[]',
      );
      setFavoriteItemIds(
        new Set(Array.isArray(parsed) ? parsed.filter(Boolean) : []),
      );
    } catch {
      setFavoriteItemIds(new Set());
    }
  }, [favoritesStorageKey]);

  useEffect(() => {
    if (!gallery) {
      return;
    }

    const nextFavorites = new Set(gallery.favoriteItemIds);
    setFavoriteItemIds(nextFavorites);

    if (favoritesStorageKey) {
      window.localStorage.setItem(
        favoritesStorageKey,
        JSON.stringify(Array.from(nextFavorites)),
      );
    }
  }, [favoritesStorageKey, gallery]);

  useEffect(() => {
    if (!canUseGuestIdentity && activeFeed !== 'approved') {
      setActiveFeed('approved');
    }
  }, [activeFeed, canUseGuestIdentity]);

  const allKnownItems = useMemo(
    () => (gallery ? [...gallery.items, ...gallery.myUploads] : []),
    [gallery],
  );
  const eventOptions = useMemo(() => {
    const seen = new Set<string>();
    return allKnownItems
      .map((item) => item.event)
      .filter((event): event is NonNullable<typeof event> => {
        if (!event || seen.has(event.id)) {
          return false;
        }
        seen.add(event.id);
        return true;
      });
  }, [allKnownItems]);
  const favoriteCount = allKnownItems.filter((item) =>
    favoriteItemIds.has(item.galleryItem.id),
  ).length;

  if (access.status !== 'ready') {
    return null;
  }

  if (status === 'loading') {
    return <StatePanel preset="loading" />;
  }

  if (status === 'error') {
    const fallback = galleryFallbackCopy(errorKind);

    return (
      <section
        className="state-panel state-panel--default"
        data-testid="guest-gallery-access-fallback"
      >
        <h2>{fallback.title}</h2>
        <p>{fallback.body}</p>
        <div className="state-panel__actions">
          <TovButton asChild>
            <NavLink to="/">
              {msg('general.navigation.return_to_overview')}
            </NavLink>
          </TovButton>
          {fallback.canRetry ? (
            <TovButton variant="secondary" onClick={() => void reload()}>
              {msg('general.actions.try_again')}
            </TovButton>
          ) : null}
        </div>
      </section>
    );
  }

  if (!gallery) {
    return null;
  }

  async function toggleFavorite(itemId: string) {
    if (
      !access.occasionSlug ||
      !access.guestToken ||
      !canUseGuestIdentity ||
      pendingFavoriteItemIds.has(itemId)
    ) {
      return;
    }

    const wasFavorite = favoriteItemIds.has(itemId);
    const nextFavorite = !wasFavorite;
    const occasionSlug = access.occasionSlug;
    const guestToken = access.guestToken;

    setPendingFavoriteItemIds((current) => new Set([...current, itemId]));
    setFavoriteItemIds((current) => {
      const next = new Set(current);

      if (nextFavorite) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }

      if (favoritesStorageKey) {
        window.localStorage.setItem(
          favoritesStorageKey,
          JSON.stringify(Array.from(next)),
        );
      }

      return next;
    });

    try {
      const favoriteIds = await guestApi.setGalleryFavorite(
        occasionSlug,
        guestToken,
        itemId,
        nextFavorite,
      );
      const nextFavorites = new Set(favoriteIds);
      setFavoriteItemIds(nextFavorites);

      if (favoritesStorageKey) {
        window.localStorage.setItem(
          favoritesStorageKey,
          JSON.stringify(Array.from(nextFavorites)),
        );
      }
    } catch {
      setFavoriteItemIds((current) => {
        const next = new Set(current);

        if (wasFavorite) {
          next.add(itemId);
        } else {
          next.delete(itemId);
        }

        if (favoritesStorageKey) {
          window.localStorage.setItem(
            favoritesStorageKey,
            JSON.stringify(Array.from(next)),
          );
        }

        return next;
      });
      notifications.error(
        msg('pages.gallery_page.gallery_upload_update_failed'),
      );
    } finally {
      setPendingFavoriteItemIds((current) => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
    }
  }

  function updateGalleryItemEngagement(
    itemId: string,
    patch: Partial<Pick<GuestGalleryItem, 'reactions' | 'comments'>>,
  ) {
    updateGallery((current) => {
      const updateItem = (item: GuestGalleryItem) =>
        item.galleryItem.id === itemId ? { ...item, ...patch } : item;

      return {
        ...current,
        items: current.items.map(updateItem),
        myUploads: current.myUploads.map(updateItem),
      };
    });
  }

  async function toggleReaction(
    itemId: string,
    reactionKind: 'heart' | 'celebrate',
    active: boolean,
  ) {
    if (
      !access.occasionSlug ||
      !access.guestToken ||
      !canUseGuestIdentity ||
      pendingReactionItemIds.has(itemId)
    ) {
      return;
    }

    setPendingReactionItemIds((current) => new Set([...current, itemId]));

    try {
      const reactions = await guestApi.setGalleryReaction(
        access.occasionSlug,
        access.guestToken,
        itemId,
        { reactionKind, active },
      );
      updateGalleryItemEngagement(itemId, { reactions });
    } catch {
      notifications.error(
        msg('pages.gallery_page.gallery_engagement_update_failed'),
      );
    } finally {
      setPendingReactionItemIds((current) => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
    }
  }

  async function submitComment(itemId: string) {
    if (
      !access.occasionSlug ||
      !access.guestToken ||
      !canUseGuestIdentity ||
      savingCommentItemIds.has(itemId)
    ) {
      return;
    }

    const body = commentDraft.trim();

    if (!body) {
      return;
    }

    setSavingCommentItemIds((current) => new Set([...current, itemId]));

    try {
      const comments = await guestApi.createGalleryComment(
        access.occasionSlug,
        access.guestToken,
        itemId,
        body,
      );
      updateGalleryItemEngagement(itemId, { comments });
      setCommentDraft('');
      notifications.success(msg('pages.gallery_page.gallery_comment_saved'));
    } catch {
      notifications.error(
        msg('pages.gallery_page.gallery_engagement_update_failed'),
      );
    } finally {
      setSavingCommentItemIds((current) => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
    }
  }

  async function createSharePackage(galleryItemId?: string) {
    if (!access.occasionSlug || !access.guestToken || !canUseGuestIdentity) {
      return;
    }

    setIsCreatingSharePackage(true);

    try {
      const response = await guestApi.createGallerySharePackage(
        access.occasionSlug,
        access.guestToken,
        galleryItemId
          ? { galleryItemIds: [galleryItemId] }
          : selectedAlbumId
            ? { albumId: selectedAlbumId }
            : {
                galleryItemIds: visibleItems.map((item) => item.galleryItem.id),
              },
      );
      await navigator.clipboard?.writeText(response.share_url);
      notifications.success(msg('pages.gallery_page.gallery_share_ready'));
    } catch {
      notifications.error(msg('pages.gallery_page.gallery_share_failed'));
    } finally {
      setIsCreatingSharePackage(false);
    }
  }

  const canUpload = gallery.uploadAllowed;
  const hasPhotos = gallery.items.length > 0;
  const uploadAction = canUpload ? (
    <GalleryUploadDialog
      occasionSlug={access.occasionSlug}
      guestToken={access.guestToken}
      uploadOptions={gallery.uploadOptions}
      uploadConstraints={gallery.uploadConstraints}
      onUploaded={async () => {
        await reload();
        setActiveFeed(canUseGuestIdentity ? 'my_uploads' : 'approved');
        setShowFilters(true);
        setLightboxIndex(null);
      }}
    />
  ) : null;
  const selectedAlbumItemIds = selectedAlbumId
    ? new Set(
        gallery.albums
          .find((entry) => entry.album.id === selectedAlbumId)
          ?.items.map((entry) => entry.galleryItem.id) ?? [],
      )
    : null;
  const feedItems =
    activeFeed === 'my_uploads' ? gallery.myUploads : gallery.items;
  const normalizedGallerySearchQuery = gallerySearchQuery.trim().toLowerCase();
  const visibleItems = feedItems
    .filter((item) => {
      if (
        activeFeed === 'favorites' &&
        !favoriteItemIds.has(item.galleryItem.id)
      ) {
        return false;
      }

      return true;
    })
    .filter((item) => {
      if (!selectedEventId) {
        return true;
      }

      return item.event?.id === selectedEventId;
    })
    .filter((item) => {
      if (!selectedAlbumItemIds || activeFeed === 'my_uploads') {
        return true;
      }

      return selectedAlbumItemIds.has(item.galleryItem.id);
    })
    .filter((item) => {
      if (!normalizedGallerySearchQuery) {
        return true;
      }

      return [
        item.galleryItem.caption ?? '',
        item.galleryItem.uploaderDisplayName ?? '',
        item.galleryItem.uploaderKind,
        item.galleryItem.moderationStatus,
        item.event?.displayName ?? '',
        item.galleryItem.id,
      ].some((value) =>
        value.toLowerCase().includes(normalizedGallerySearchQuery),
      );
    });
  const hasActiveFilters =
    activeFeed !== 'approved' ||
    selectedEventId !== '' ||
    selectedAlbumId !== null ||
    normalizedGallerySearchQuery !== '';
  async function updateSelectedUpload() {
    if (!selectedUpload || !access.occasionSlug || !access.guestToken) {
      return;
    }
    const occasionSlug = access.occasionSlug;
    const guestToken = access.guestToken;

    setIsSavingUpload(true);

    try {
      await guestApi.updateGalleryUpload(
        occasionSlug,
        guestToken,
        selectedUpload.galleryItem.id,
        {
          caption: selectedUploadCaption,
        },
      );
      await reload();
      notifications.success(msg('pages.gallery_page.gallery_upload_updated'));
      setSelectedUpload(null);
    } catch {
      notifications.error(
        msg('pages.gallery_page.gallery_upload_update_failed'),
      );
    } finally {
      setIsSavingUpload(false);
    }
  }

  async function removeSelectedUpload() {
    if (!selectedUpload || !access.occasionSlug || !access.guestToken) {
      return;
    }
    const occasionSlug = access.occasionSlug;
    const guestToken = access.guestToken;

    setIsSavingUpload(true);

    try {
      await guestApi.deleteGalleryUpload(
        occasionSlug,
        guestToken,
        selectedUpload.galleryItem.id,
      );
      await reload();
      notifications.success(msg('pages.gallery_page.gallery_upload_removed'));
      setSelectedUpload(null);
    } catch {
      notifications.error(
        msg('pages.gallery_page.gallery_upload_remove_failed'),
      );
    } finally {
      setIsSavingUpload(false);
    }
  }

  if (!hasPhotos && !canUpload && gallery.myUploads.length === 0) {
    return (
      <section className="state-panel state-panel--default">
        <h2>{msg('pages.gallery_page.gallery_no_photos_title')}</h2>
        <p>{msg('pages.gallery_page.hosts_have_not_shared_photos_yet')}</p>
        <div className="state-panel__actions">
          <TovButton asChild>
            <NavLink to="/">
              {msg('general.navigation.return_to_overview')}
            </NavLink>
          </TovButton>
        </div>
      </section>
    );
  }

  return (
    <PageGrid>
      <PageWideSection className="gallery-header">
        <div className="gallery-header__intro">
          <PageEyebrow>{msg('general.modules.gallery.name')}</PageEyebrow>
          <h2>{msg('pages.gallery_page.photos')}</h2>
        </div>
        <div className="gallery-header__actions">
          {uploadAction}
          <TovButton
            type="button"
            variant="secondary"
            onClick={() => setShowFilters((current) => !current)}
          >
            {showFilters
              ? msg('pages.gallery_page.hide_filters')
              : msg('pages.gallery_page.filter')}
          </TovButton>
        </div>
      </PageWideSection>

      {showFilters ? (
        <>
          <GalleryControls
            activeFeed={activeFeed}
            eventId={selectedEventId}
            eventOptions={eventOptions}
            favoriteCount={favoriteCount}
            itemCount={visibleItems.length}
            searchQuery={gallerySearchQuery}
            onFeedChange={(feed) => {
              setActiveFeed(canUseGuestIdentity ? feed : 'approved');
              setLightboxIndex(null);
            }}
            showPersonalFeeds={canUseGuestIdentity}
            onEventChange={(eventId) => {
              setSelectedEventId(eventId);
              setLightboxIndex(null);
            }}
            onSearchChange={(query) => {
              setGallerySearchQuery(query);
              setLightboxIndex(null);
            }}
          />
          <AlbumsRail
            albums={gallery.albums}
            items={gallery.items}
            myUploads={gallery.myUploads}
            selectedAlbumId={selectedAlbumId}
            onSelectAlbum={(albumId) => {
              setSelectedAlbumId(albumId);
              setLightboxIndex(null);
            }}
          />
        </>
      ) : null}

      {gallery.pendingUploadSessions.length > 0 ? (
        <PageWideSection className="gallery-resume">
          <PageEyebrow>
            {msg('pages.gallery_page.gallery_pending_uploads')}
          </PageEyebrow>
          <ul>
            {gallery.pendingUploadSessions.map((session) => (
              <li key={session.id}>
                <span>{session.fileName}</span>
                <TovButton
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    void guestApi
                      .completeGalleryUpload(
                        access.occasionSlug,
                        access.guestToken,
                        session.id,
                      )
                      .then(() => reload())
                  }
                >
                  {msg('pages.gallery_page.gallery_resume_upload')}
                </TovButton>
              </li>
            ))}
          </ul>
        </PageWideSection>
      ) : null}

      <PageWideSection>
        {visibleItems.length > 0 ? (
          <>
            <GalleryPhotoGrid
              favoriteItemIds={favoriteItemIds}
              items={visibleItems}
              pendingFavoriteItemIds={pendingFavoriteItemIds}
              showFavorites={canUseGuestIdentity}
              onOpen={setLightboxIndex}
              onToggleFavorite={toggleFavorite}
            />
            {activeFeed === 'approved' && gallery.pageInfo.nextCursor ? (
              <div className="gallery-load-more">
                <TovButton
                  type="button"
                  variant="secondary"
                  disabled={isLoadingMore}
                  onClick={() => void loadMore()}
                >
                  {isLoadingMore
                    ? msg('pages.gallery_page.gallery_loading_more')
                    : msg('components.data_views.data_view_table.load_more')}
                </TovButton>
              </div>
            ) : null}
          </>
        ) : (
          <GalleryEmptyState
            canUpload={canUpload}
            isFiltered={hasActiveFilters}
          />
        )}
      </PageWideSection>

      {canUseGuestIdentity ? (
        <MyUploadsPanel
          myUploads={gallery.myUploads}
          onEditUpload={(item) => {
            setSelectedUpload(item);
            setSelectedUploadCaption(item.galleryItem.caption ?? '');
          }}
        />
      ) : null}

      <GalleryLightbox
        commentDraft={commentDraft}
        favoriteItemIds={favoriteItemIds}
        isCreatingSharePackage={isCreatingSharePackage}
        items={visibleItems}
        openIndex={lightboxIndex}
        pendingFavoriteItemIds={pendingFavoriteItemIds}
        pendingReactionItemIds={pendingReactionItemIds}
        savingCommentItemIds={savingCommentItemIds}
        canUseGuestIdentity={canUseGuestIdentity}
        onCommentDraftChange={setCommentDraft}
        onOpenChange={setLightboxIndex}
        onSubmitComment={submitComment}
        onCreateSharePackage={(itemId) => void createSharePackage(itemId)}
        onToggleFavorite={toggleFavorite}
        onToggleReaction={toggleReaction}
      />

      <Dialog
        open={selectedUpload !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isSavingUpload) {
            setSelectedUpload(null);
          }
        }}
        title={msg('pages.gallery_page.gallery_edit_upload')}
        footer={
          <>
            <TovButton
              type="button"
              variant="secondary"
              disabled={isSavingUpload}
              onClick={() => void removeSelectedUpload()}
            >
              {msg('general.actions.remove')}
            </TovButton>
            <TovButton
              type="button"
              disabled={isSavingUpload}
              onClick={() => void updateSelectedUpload()}
            >
              {isSavingUpload
                ? msg('general.states.saving')
                : msg('general.actions.save_changes')}
            </TovButton>
          </>
        }
      >
        <div className="gallery-upload-form">
          <label className="gallery-upload-form__field">
            <span>{msg('pages.gallery_page.pages_gallery_page_caption')}</span>
            <Input
              disabled={isSavingUpload}
              value={selectedUploadCaption}
              onChange={(event) => setSelectedUploadCaption(event.target.value)}
            />
          </label>
        </div>
      </Dialog>
    </PageGrid>
  );
}
